/**
 * Rounded-Top Bolster / Side-Panel Cushion Configurator — UNIVERSAL-BLOCK version
 * uscushion.com · 2026-10
 *
 * Runs INSIDE the shared cushion-configurator-block.liquid (formula_type == 'bolster').
 * Same integration pattern as cushion-configurator-lcut.js / -polygon.js.
 *
 * Shape (side profile, extruded by Thickness): a tall narrow panel — flat bottom, two
 *   vertical sides of DIFFERENT heights, and a quarter-round ("bullnose") top that runs
 *   the full width from the taller side's top corner down to the shorter side's edge.
 *
 *   Customer inputs (inches) — ALL straight lines, the customer never measures the curve:
 *     w   = width                 (e.g. 8)
 *     hL  = left side height      (e.g. 45)
 *     hR  = right side height     (e.g. 37)
 *     T   = thickness
 *   The top curve is DERIVED: a quarter ellipse, horizontal semi-axis = w,
 *   vertical semi-axis = |hL - hR|. Taller side = flat top corner (horizontal tangent);
 *   it sweeps across the full width and meets the shorter side's edge vertically.
 *   When hL == hR the top is flat (plain rectangle). When w == |hL-hR| the arc is a
 *   true quarter circle (radius w). Example: 8 / 45 / 37 → R8 quarter circle, arc 12.57".
 *
 *   Pricing: foam & fabric cut from the bounding rectangle (w × tallHeight), offcut of
 *   the rounded corner scrapped — same bench-formula / bounding-box口径 as round/polygon.
 */
(function () {
  'use strict';

  /* ─── Cost constants (same as bench / round / lcut) ─── */
  var FABRIC_PRICE_RMB  = 25;
  var FABRIC_RATE_RMB   = { marine: 54 };
  function fabricRate() {
    var g = (state.fabric && state.fabric.tags) ? extractTag(state.fabric.tags, 'grade') : '';
    return FABRIC_RATE_RMB[g] || FABRIC_PRICE_RMB;
  }
  var FOAM_PRICE_RMB    = 1750;
  var FOAM_DENSITY      = 35;
  var FABRIC_GSM        = 400;
  var SHIPPING_RMB      = 90;
  var ACCESSORIES_RMB   = 30;
  var EXCHANGE_RATE     = 6.8;
  var PAYMENT_FEE_RATE  = 0.049;
  var TARGET_COST_RATIO = 0.40;
  var MARINE_COST_RATIO = 0.38;
  var COVER_COST_RATIO  = 0.55;
  function targetRatio() {
    var g = (state.fabric && state.fabric.tags) ? extractTag(state.fabric.tags, 'grade') : '';
    return g === 'marine' ? MARINE_COST_RATIO : TARGET_COST_RATIO;
  }
  var MIN_PRICE         = 45;
  /* Irregular-shape surcharge: curved end = extra foam shaping + curved cover sewing.
     Added on top of the bounding-rectangle base = max(10% of base, $20). */
  var SHAPE_SURCHARGE_PCT = 0.10;
  var SHAPE_SURCHARGE_MIN = 20;

  /* ─── Addon pricing (USD) ─── */
  var ADDON = {
    piping:      { none: 0, single: 5, double: 8 },
    pipingColor: { match: 0, white: 5, black: 5, navy: 5, cream: 5 },
    straps:      { none: 0, two: 2, four: 4 },
    zipper:      { regular: 0, invisible: 0 },
    antislip:    { none: 0, black_pad: 5, beige_pad: 5, velcro: 9 },
    throwPillow: {
      none: 0,
      '2pcs_12x20': 29, '2pcs_16x16': 29, '2pcs_18x18': 32,
      '2pcs_20x20': 35, '2pcs_24x24': 38, '2pcs_custom': 40,
      '4pcs_12x20': 51, '4pcs_16x16': 51, '4pcs_18x18': 57,
      '4pcs_20x20': 63, '4pcs_24x24': 67, '4pcs_custom': 70
    },
    pillowCovers: {
      none: 0,
      '2pcs_12x20': 19, '2pcs_16x16': 19, '2pcs_18x18': 21,
      '2pcs_20x20': 24, '2pcs_24x24': 29, '2pcs_custom': 31,
      '4pcs_12x20': 32, '4pcs_16x16': 32, '4pcs_18x18': 36,
      '4pcs_20x20': 42, '4pcs_24x24': 52, '4pcs_custom': 55
    },
    armrestPillow: { none: 0, square_2pcs: 30, round_2pcs: 30 }
  };
  /* ─── Units (depolster.de): inputs in cm, pricing engine unchanged (inch). inch→cm only for display. ─── */
  var CM_PER_IN = 2.54;
  function cmS(inch) { return round1(inch * CM_PER_IN); }
  function parseNum(v) { return parseFloat(String(v == null ? '' : v).replace(',', '.')) || 0; }
  function round1(n) { return Math.round(n * 10) / 10; }
  var PILLOW_SIZE = { '12x20': '30×50 cm', '16x16': '40×40 cm', '18x18': '45×45 cm', '20x20': '50×50 cm', '24x24': '60×60 cm', 'custom': 'Sondermaß' };
  function pillowLabel(v) { var m = /^(\d)pcs_(.+)$/.exec(v); return m ? (m[1] + ' Stück ' + (PILLOW_SIZE[m[2]] || m[2])) : v; }
  var ARMREST_LABEL = { square_2pcs: 'Eckig — 2 Stück', round_2pcs: 'Rund — 2 Stück' };


  var LABELS = {
    none: 'Keine', single: 'Einfacher Keder', double: 'Doppelter Keder',
    match: 'Passend zum Stoff', white: 'Weißer Keder', black: 'Schwarzer Keder', navy: 'Marineblauer Keder', cream: 'Cremefarbener Keder',
    two: '2 Bänder', four: '4 Bänder',
    regular: 'Standard-Reißverschluss', invisible: 'Nahtverdeckter Reißverschluss',
    velcro: 'Klettband', black_pad: 'Schwarze Unterlage', beige_pad: 'Beige Unterlage'
  };

  var FABRIC_COLORS = {
    'blue':'#2c3e6b','navy':'#1a2540','teal':'#2a7a6b','green':'#4a7a2a',
    'sage':'#a8b89a','red':'#b83232','coral':'#d85a45','orange':'#d4783a',
    'yellow':'#d4a83a','gold':'#c8963a','pink':'#d4649a','purple':'#6a4a9a',
    'lavender':'#9a8ab8','brown':'#7a5a3a','camel':'#c8a878','tan':'#c8b08a',
    'beige':'#d4c8a8','cream':'#ede8df','ivory':'#f0ead8','white':'#f5f5f0',
    'gray':'#9a9a9a','grey':'#9a9a9a','charcoal':'#4a4a4a','black':'#1a1a1a',
    'mint':'#98d4b4','rust':'#b7502a','mustard':'#c8a020','olive':'#6b702a',
    'blush':'#de9898','mauve':'#b07898','burgundy':'#6b1a2a','taupe':'#8a7a6a',
    'sand':'#c8b898','silver':'#bfc0c2','steel blue':'#4a7a98',
    'dark olive':'#4a4a2a','dark green':'#2a5a2a','dark brown':'#4a3020',
    'dark blue':'#1a2a5a','dark teal':'#1a5a5a','dark gray':'#5a5a5a',
    'light gray':'#c8c8c8','gray blue':'#6a7a8a','yellow green':'#8ab82a',
    'gray black':'#3a3a3a'
  };

  /* ─── State ─── */
  var state = {
    shape: 'single',
    w: 0, hL: 0, hR: 0, length: 0, thickness: 0,
    fabric: null, piping: '', pipingColor: 'match', straps: '',
    zipper: 'regular', zipperPlacement: '', antislip: '',
    extraCovers: '0', throwPillow: 'none', pillowCovers: 'none',
    armrestPillow: 'none', notes: ''
  };

  var els = {};

  /* ─── Color helpers ─── */
  function hexToRgb(hex) {
    hex = hex.replace('#', '');
    return [parseInt(hex.substring(0, 2), 16), parseInt(hex.substring(2, 4), 16), parseInt(hex.substring(4, 6), 16)];
  }
  function rgbToHex(r, g, b) {
    return '#' + [r, g, b].map(function (c) {
      var x = Math.round(Math.max(0, Math.min(255, c))).toString(16);
      return x.length === 1 ? '0' + x : x;
    }).join('');
  }
  function lighten(hex, r) { var c = hexToRgb(hex); return rgbToHex(c[0] + (255 - c[0]) * r, c[1] + (255 - c[1]) * r, c[2] + (255 - c[2]) * r); }
  function darken(hex, r)  { var c = hexToRgb(hex); return rgbToHex(c[0] * (1 - r), c[1] * (1 - r), c[2] * (1 - r)); }
  function extractTag(tags, prefix) {
    if (!tags) return '';
    for (var i = 0; i < tags.length; i++) {
      var t = (typeof tags[i] === 'string' ? tags[i] : '').toLowerCase().trim();
      if (t.indexOf(prefix + ':') === 0) return tags[i].split(':')[1].trim().toLowerCase();
    }
    return '';
  }

  /* ═══════════════════════════════════════════════════════════
     Geometry — tall panel, flat bottom, vertical sides hL (left) & hR (right),
     quarter-ellipse top tessellated into a polyline. Coordinates y-down
     (apex/top near y=0, bottom at y=hi); centered on the bounding box.
     ═══════════════════════════════════════════════════════════ */
  var ARC_N = 28;
  function centerRaw(raw) {
    var minX = 1e9, minY = 1e9, maxX = -1e9, maxY = -1e9;
    raw.forEach(function (p) { minX = Math.min(minX, p[0]); maxX = Math.max(maxX, p[0]); minY = Math.min(minY, p[1]); maxY = Math.max(maxY, p[1]); });
    var cx = (minX + maxX) / 2, cy = (minY + maxY) / 2;
    return { verts: raw.map(function (p) { return [p[0] - cx, p[1] - cy]; }), ctr: function (p) { return [p[0] - cx, p[1] - cy]; } };
  }

  /* SHAPE 1 — one rounded end: flat LEFT end (height W), TOP edge HL, BOTTOM edge HR,
     quarter-ellipse RIGHT end. */
  function geomSingle(d) {
    var W = d.w, HL = d.hL, HR = d.hR;
    var errs = [];
    if (!(W > 0 && HL > 0 && HR > 0)) errs.push('alle Maße');
    var hi = Math.max(HL, HR), lo = Math.min(HL, HR), k, th;
    var raw = [];
    raw.push([0, 0]);
    raw.push([HL, 0]);
    for (k = 1; k <= ARC_N; k++) { th = Math.PI / 2 * k / ARC_N; raw.push([HR + (HL - HR) * Math.cos(th), W * Math.sin(th)]); }
    raw.push([0, W]);
    var c = centerRaw(raw);
    var key = {
      topMid:     c.ctr([HL / 2, 0]),
      botMid:     c.ctr([HR / 2, W]),
      leftEndMid: c.ctr([0, W / 2]),
      curveMid:   c.ctr([HR + (HL - HR) * Math.cos(Math.PI / 4), W * Math.sin(Math.PI / 4)])
    };
    var a = Math.abs(HL - HR), bb = W;
    var arcLen = a <= 0 ? W : (Math.PI * (3 * (a + bb) - Math.sqrt((3 * a + bb) * (a + 3 * bb)))) / 4;
    return {
      verts: c.verts, key: key, shape: 'single', tallLeft: HL >= HR, W: W, HL: HL, HR: HR, hi: hi, lo: lo,
      arcLen: arcLen, overallLen: Math.max(HL, HR), overallW: W, valid: errs.length === 0, errs: errs
    };
  }

  /* SHAPE 2 — both ends rounded (symmetric "pill" / stadium): length L, width W,
     two half-circle ends of radius W/2. */
  function geomPill(d) {
    var L = d.length, W = d.w;
    var errs = [];
    if (!(L > 0 && W > 0)) errs.push('alle Maße');
    else if (L < W - 0.001) errs.push('Länge muss mindestens so groß wie die Breite sein (die Enden sind Halbkreise)');
    var r = W / 2, cxL = r, cxR = Math.max(L - r, r), cyMid = r, k, a;
    var raw = [];
    raw.push([cxL, 0]);
    raw.push([cxR, 0]);                                                   // top straight
    for (k = 1; k <= ARC_N; k++) { a = -Math.PI / 2 + Math.PI * k / ARC_N; raw.push([cxR + r * Math.cos(a), cyMid + r * Math.sin(a)]); } // right cap
    raw.push([cxL, W]);                                                   // bottom straight
    for (k = 1; k < ARC_N; k++) { a = Math.PI / 2 + Math.PI * k / ARC_N; raw.push([cxL + r * Math.cos(a), cyMid + r * Math.sin(a)]); }   // left cap
    var c = centerRaw(raw);
    var key = {
      topMid:     c.ctr([L / 2, 0]),
      leftEndMid: c.ctr([0, cyMid]),
      curveMid:   c.ctr([cxR + r, cyMid])
    };
    return {
      verts: c.verts, key: key, shape: 'pill', W: W, length: L, roundR: r,
      arcLen: Math.PI * W, overallLen: L, overallW: W, valid: errs.length === 0, errs: errs
    };
  }

  /* SHAPE 3 — one symmetric half-round end + one flat end ("D" / half-stadium):
     length L, width W, one semicircle end of radius W/2, the other end flat. */
  function geomDcap(d) {
    var L = d.length, W = d.w;
    var errs = [];
    if (!(L > 0 && W > 0)) errs.push('alle Maße');
    else if (L < W / 2 + 0.001) errs.push('Länge muss größer als der Halbkreis-Radius sein (Breite ÷ 2)');
    var r = W / 2, cxR = Math.max(L - r, 0), cyMid = r, k, a;
    var raw = [];
    raw.push([0, 0]);                                                     // flat end, top-left
    raw.push([cxR, 0]);                                                   // top straight
    for (k = 1; k <= ARC_N; k++) { a = -Math.PI / 2 + Math.PI * k / ARC_N; raw.push([cxR + r * Math.cos(a), cyMid + r * Math.sin(a)]); } // right semicircle
    raw.push([0, W]);                                                     // flat end, bottom-left
    var c = centerRaw(raw);
    var key = {
      topMid:     c.ctr([cxR / 2, 0]),
      leftEndMid: c.ctr([0, cyMid]),
      curveMid:   c.ctr([cxR + r, cyMid])
    };
    return {
      verts: c.verts, key: key, shape: 'dcap', W: W, length: L, roundR: r,
      arcLen: Math.PI * r, overallLen: L, overallW: W, valid: errs.length === 0, errs: errs
    };
  }

  function geom(d) {
    if (state.shape === 'pill') return geomPill(d);
    if (state.shape === 'dcap') return geomDcap(d);
    return geomSingle(d);
  }

  function bounds(verts) {
    var minX = 1e9, minY = 1e9, maxX = -1e9, maxY = -1e9, i;
    for (i = 0; i < verts.length; i++) {
      minX = Math.min(minX, verts[i][0]); maxX = Math.max(maxX, verts[i][0]);
      minY = Math.min(minY, verts[i][1]); maxY = Math.max(maxY, verts[i][1]);
    }
    return { w: maxX - minX, h: maxY - minY, mx: Math.max(Math.abs(minX), Math.abs(maxX)), my: Math.max(Math.abs(minY), Math.abs(maxY)) };
  }

  /* ─── Pricing — single bounding rectangle (w × tall height), bench formula ─── */
  function calcPiecePrice(L, Wd, T) {
    if (!L || !Wd || !T) return 0;
    var Lm = L / 39.37, Wm = Wd / 39.37, Tm = T / 39.37;
    var fabricArea = Lm * Wm * 2 + Lm * Tm * 2 + Wm * Tm * 2;
    var foamVolume = Lm * Wm * Tm;
    var totalWeight = foamVolume * FOAM_DENSITY + fabricArea * FABRIC_GSM / 1000;
    var totalRMB = fabricArea * fabricRate() + foamVolume * FOAM_PRICE_RMB + totalWeight * SHIPPING_RMB + ACCESSORIES_RMB;
    return totalRMB / EXCHANGE_RATE / (1 - PAYMENT_FEE_RATE) / targetRatio();
  }
  function calcPrice() {
    var g = geom(state);
    if (!g.valid || !state.thickness) return null;
    var base = calcPiecePrice(g.overallLen, g.overallW, state.thickness);
    var withShape = base + Math.max(base * SHAPE_SURCHARGE_PCT, SHAPE_SURCHARGE_MIN);
    return Math.max(withShape * (window.__CC_COVER_FACTOR || 1), MIN_PRICE);
  }

  function coverPiece(L, Wd, T) {
    if (!L || !Wd || !T) return 0;
    var Lm = L / 39.37, Wm = Wd / 39.37, Tm = T / 39.37;
    var fabricArea = Lm * Wm * 2 + Lm * Tm * 2 + Wm * Tm * 2;
    var weight = fabricArea * FABRIC_GSM / 1000;
    var totalRMB = fabricArea * fabricRate() + weight * SHIPPING_RMB + ACCESSORIES_RMB;
    return totalRMB / EXCHANGE_RATE / (1 - PAYMENT_FEE_RATE) / COVER_COST_RATIO;
  }
  function extraCoverUnit() {
    var g = geom(state);
    if (!g.valid || !state.thickness) return 0;
    return Math.round(coverPiece(g.overallLen, g.overallW, state.thickness));
  }

  function updateExtraCoverLabels() {
    if (!els.extraCovers) return;
    var unit = extraCoverUnit();
    var opts = els.extraCovers.options;
    for (var i = 0; i < opts.length; i++) {
      var n = parseInt(opts[i].value) || 0;
      if (n === 0) { opts[i].text = 'Kein zusätzlicher Bezug'; continue; }
      var label = (n === 1 ? '1 Set' : n + ' Sets');
      opts[i].text = unit > 0 ? label + ' (+' + (unit * n) + ' €)' : label;
    }
  }

  function getAddonTotal() {
    return (ADDON.piping[state.piping] || 0)
      + (ADDON.pipingColor[state.pipingColor] || 0)
      + (ADDON.straps[state.straps] || 0) + (ADDON.zipper[state.zipper] || 0)
      + (ADDON.antislip[state.antislip] || 0) + extraCoverUnit() * (parseInt(state.extraCovers) || 0)
      + (ADDON.throwPillow[state.throwPillow] || 0)
      + (ADDON.pillowCovers[state.pillowCovers] || 0)
      + (ADDON.armrestPillow[state.armrestPillow] || 0);
  }

  function hasDims() { return geom(state).valid && state.thickness >= 1; }
  function isComplete() { return hasDims() && state.fabric && state.piping && state.straps && state.antislip; }

  /* ═══════════════════════════════════════════════════════════
     SVG Preview — profile top face + extruded front (bottom) wall
     viewBox 440×290, center [210,120]
     ═══════════════════════════════════════════════════════════ */
  function drawCushion() {
    var svg = els.dimSvg;
    if (!svg) return;

    var T = state.thickness;
    var g = geom(state);
    var empty = state.shape === 'single' ? !(state.w && state.hL && state.hR) : !(state.length && state.w);

    function clamp(v, mn, mx) { return Math.max(mn, Math.min(mx, v)); }
    function pt(a) { return a[0].toFixed(1) + ',' + a[1].toFixed(1); }
    function f(n)  { return n.toFixed(1); }

    var topC = '#EDE9E0', frontC = '#D5D0C2', strokeC = '#2A2A28', fabricLum = 200;
    if (state.fabric && state.fabric.tags) {
      var colorTag = extractTag(state.fabric.tags, 'color');
      var baseHex = FABRIC_COLORS[colorTag];
      if (baseHex) {
        topC = lighten(baseHex, 0.50); frontC = lighten(baseHex, 0.20); strokeC = darken(baseHex, 0.45);
        var sc0 = hexToRgb(strokeC);
        if ((sc0[0] + sc0[1] + sc0[2]) / 3 > 120) strokeC = '#2A2A28';
        var fc = hexToRgb(baseHex); fabricLum = (fc[0] + fc[1] + fc[2]) / 3;
      }
    }

    var h = '';
    h += '<defs><marker id="bl-arr" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">'
      + '<path d="M1 1.5L8.5 5L1 8.5" fill="none" stroke="context-stroke" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></marker></defs>';

    /* Always draw a shape. Before any input, show a muted SAMPLE so the customer
       immediately sees what they're configuring; it goes live as they type. */
    var gd = empty ? (state.shape === 'pill' ? geomPill({ length: 40, w: 12 }) : state.shape === 'dcap' ? geomDcap({ length: 38, w: 12 }) : geomSingle({ w: 10, hL: 42, hR: 28 })) : g;
    var Teff = empty ? 3 : T;
    if (empty) { topC = '#ECE8DF'; frontC = '#DCD7CB'; strokeC = '#B9B3A5'; fabricLum = 200; }

    var verts = gd.verts, b = bounds(verts), i, j;
    var cx = 220, cy = 118;
    var sc = clamp(Math.min(380 / b.w, 150 / b.h), 1.2, 10);
    var thickPx = clamp((Teff || 3) * 6, 8, 36);

    var topPts = [], botPts = [];
    for (i = 0; i < verts.length; i++) {
      var x = cx + verts[i][0] * sc, y = cy + verts[i][1] * sc;
      topPts.push([x, y]); botPts.push([x, y + thickPx]);
    }

    function inPoly(p) {
      var inside = false, n = topPts.length;
      for (var a = 0, c = n - 1; a < n; c = a++) {
        var xi = topPts[a][0], yi = topPts[a][1], xj = topPts[c][0], yj = topPts[c][1];
        if (((yi > p[1]) !== (yj > p[1])) && (p[0] < (xj - xi) * (p[1] - yi) / (yj - yi) + xi)) inside = !inside;
      }
      return inside;
    }

    /* Classify edges: front (faces down → thickness wall) / back (faces up → zipper) */
    var frontEdges = [], backEdges = [];
    for (i = 0; i < topPts.length; i++) {
      j = (i + 1) % topPts.length;
      var ex = topPts[j][0] - topPts[i][0], ey = topPts[j][1] - topPts[i][1];
      var el = Math.hypot(ex, ey) || 1, nx = ey / el, ny = -ex / el;
      var mx0 = (topPts[i][0] + topPts[j][0]) / 2, my0 = (topPts[i][1] + topPts[j][1]) / 2;
      var outGt = !inPoly([mx0 + nx * 3, my0 + ny * 3]);
      var oy = outGt ? ny : -ny;
      if (oy > 0.12) frontEdges.push(i);
      else if (oy < -0.12) backEdges.push(i);
    }

    /* ── Straps (hang from the bottom edge) ── */
    if ((state.straps === 'two' || state.straps === 'four') && frontEdges.length) {
      var tieC = fabricLum < 100 ? '#8A8A85' : '#4A4A47', hangLen = 15;
      function drawTie(bx, by, dir) {
        var spread = 4;
        h += '<path d="M' + f(bx - spread * 0.5) + ' ' + f(by) + ' Q' + f(bx - spread + dir * 3) + ' ' + f(by + hangLen * 0.5) + ' ' + f(bx - spread * 1.5 + dir * 5) + ' ' + f(by + hangLen) + '" fill="none" stroke="' + tieC + '" stroke-width="1.6" stroke-linecap="round"/>';
        h += '<path d="M' + f(bx + spread * 0.5) + ' ' + f(by) + ' Q' + f(bx + spread + dir * 2) + ' ' + f(by + hangLen * 0.5) + ' ' + f(bx + spread * 1.5 + dir * 4) + ' ' + f(by + hangLen) + '" fill="none" stroke="' + tieC + '" stroke-width="1.6" stroke-linecap="round"/>';
      }
      var fe = frontEdges[0], fk = (fe + 1) % topPts.length;
      var mcx = (botPts[fe][0] + botPts[fk][0]) / 2, mcy = (botPts[fe][1] + botPts[fk][1]) / 2;
      drawTie(mcx, mcy, 0);
      if (state.straps === 'four') {
        drawTie(botPts[fe][0] * 0.65 + mcx * 0.35, botPts[fe][1] * 0.65 + mcy * 0.35, -1);
        drawTie(botPts[fk][0] * 0.65 + mcx * 0.35, botPts[fk][1] * 0.65 + mcy * 0.35, 1);
      }
    }

    /* ── Side band: wall quad per front edge (the flat bottom) ── */
    frontEdges.forEach(function (ei) {
      var k = (ei + 1) % topPts.length;
      h += '<path d="M ' + pt(topPts[ei]) + ' L ' + pt(topPts[k]) + ' L ' + pt(botPts[k]) + ' L ' + pt(botPts[ei]) + ' Z" fill="' + frontC + '" stroke="' + strokeC + '" stroke-width="1.4" stroke-linejoin="round"/>';
    });

    /* ── Top face ── */
    var topPath = 'M ' + pt(topPts[0]);
    for (i = 1; i < topPts.length; i++) topPath += ' L ' + pt(topPts[i]);
    topPath += ' Z';
    h += '<path d="' + topPath + '" fill="' + topC + '" stroke="' + strokeC + '" stroke-width="1.4" stroke-linejoin="round"/>';

    /* ── Zipper: along back/top edges (follows the curve) ── */
    if (backEdges.length) {
      var zx = 0, zy = 1e9, zn = 0;
      backEdges.forEach(function (ei) {
        var k = (ei + 1) % topPts.length;
        h += '<path d="M ' + pt(topPts[ei]) + ' L ' + pt(topPts[k]) + '" fill="none" stroke="#3A7D2C" stroke-width="3" stroke-linecap="round" opacity="0.92"/>';
        zx += topPts[ei][0]; zy = Math.min(zy, topPts[ei][1]); zn++;
      });
      if (zn) h += '<text x="' + f(zx / zn) + '" y="' + f(zy - 6) + '" text-anchor="middle" font-size="8.5" font-family="Poppins,sans-serif" font-weight="600" fill="#3A7D2C">Reißverschluss</text>';
    }

    /* ── Piping ── */
    if (state.piping === 'single' || state.piping === 'double') {
      var pipC = '#F5F1E8', pw = 2.2;
      h += '<path d="' + topPath + '" fill="none" stroke="' + pipC + '" stroke-width="' + pw + '" stroke-linejoin="round" opacity="0.85"/>';
      if (state.piping === 'double') {
        frontEdges.forEach(function (ei) {
          var k = (ei + 1) % topPts.length;
          h += '<path d="M ' + pt(botPts[ei]) + ' L ' + pt(botPts[k]) + '" fill="none" stroke="' + pipC + '" stroke-width="' + pw + '" stroke-linecap="round" opacity="0.85"/>';
        });
      }
    }

    /* ── Anti-slip strip along the bottom ── */
    if (state.antislip && state.antislip !== 'none' && frontEdges.length) {
      var padC, padOp;
      if (state.antislip === 'black_pad') { padC = '#1A1A18'; padOp = 0.32; }
      else if (state.antislip === 'beige_pad') { padC = '#C8B898'; padOp = 0.4; }
      else { padC = '#4A4020'; padOp = 0.26; }
      var stripH = 3;
      frontEdges.forEach(function (ei) {
        var k = (ei + 1) % topPts.length;
        h += '<path d="M ' + pt(botPts[ei]) + ' L ' + pt(botPts[k]) + ' L ' + pt([botPts[k][0], botPts[k][1] + stripH]) + ' L ' + pt([botPts[ei][0], botPts[ei][1] + stripH]) + ' Z" fill="' + padC + '" opacity="' + padOp + '"/>';
      });
    }

    /* ── Dimension labels (by geometry key points; the curve is auto) ── */
    var lc = '#8A6A3A';
    function kp(p) { return [cx + p[0] * sc, cy + p[1] * sc]; }
    var tM = kp(gd.key.topMid), lM = kp(gd.key.leftEndMid), cM = kp(gd.key.curveMid);
    if (gd.shape === 'pill' || gd.shape === 'dcap') {
      // Length (top) + Width (left end); ends auto-rounded
      h += '<text x="' + f(tM[0]) + '" y="' + f(tM[1] - 19) + '" text-anchor="middle" font-size="11" font-family="Poppins,sans-serif" font-weight="600" fill="' + lc + '">' + (empty ? 'Länge' : 'Länge ' + cmS(state.length) + ' cm') + '</text>';
      h += '<text x="' + f(lM[0] - 12) + '" y="' + f(lM[1]) + '" text-anchor="middle" font-size="10" font-family="Poppins,sans-serif" font-weight="600" fill="' + lc + '" transform="rotate(-90 ' + f(lM[0] - 12) + ' ' + f(lM[1]) + ')">' + (empty ? 'Breite' : 'B ' + cmS(state.w) + ' cm') + '</text>';
      h += '<text x="' + f(cM[0] + 12) + '" y="' + f(cM[1]) + '" text-anchor="start" font-size="9" font-family="Poppins,sans-serif" font-style="italic" fill="#B06A2A">' + (gd.shape === 'pill' ? 'gerundete Enden (auto)' : 'gerundetes Ende (auto)') + '</text>';
    } else {
      var bM = kp(gd.key.botMid);
      h += '<text x="' + f(tM[0]) + '" y="' + f(tM[1] - 8) + '" text-anchor="middle" font-size="11" font-family="Poppins,sans-serif" font-weight="600" fill="' + lc + '">' + (empty ? 'Seite links' : 'Links ' + cmS(state.hL) + ' cm') + '</text>';
      h += '<text x="' + f(bM[0]) + '" y="' + f(bM[1] + thickPx + 14) + '" text-anchor="middle" font-size="11" font-family="Poppins,sans-serif" font-weight="600" fill="' + lc + '">' + (empty ? 'Seite rechts' : 'Rechts ' + cmS(state.hR) + ' cm') + '</text>';
      h += '<text x="' + f(lM[0] - 12) + '" y="' + f(lM[1]) + '" text-anchor="middle" font-size="10" font-family="Poppins,sans-serif" font-weight="600" fill="' + lc + '" transform="rotate(-90 ' + f(lM[0] - 12) + ' ' + f(lM[1]) + ')">' + (empty ? 'Breite' : 'B ' + cmS(state.w) + ' cm') + '</text>';
      h += '<text x="' + f(cM[0] + 14) + '" y="' + f(cM[1]) + '" text-anchor="start" font-size="9" font-family="Poppins,sans-serif" font-style="italic" fill="#B06A2A">gebogenes Ende (auto)</text>';
    }

    /* ── Thickness arrow (rightmost bottom corner) ── */
    var thkC = '#3d5e3f', ls = 10;
    var ti = 0, tbx = -1e9;
    frontEdges.forEach(function (ei) {
      var k = (ei + 1) % topPts.length;
      if (topPts[ei][0] > tbx) { tbx = topPts[ei][0]; ti = ei; }
      if (topPts[k][0] > tbx) { tbx = topPts[k][0]; ti = k; }
    });
    var thkPt = topPts[ti], thkBt = botPts[ti], hug = 16;
    h += '<line x1="' + f(thkPt[0]) + '" y1="' + f(thkPt[1]) + '" x2="' + f(thkPt[0] + hug) + '" y2="' + f(thkPt[1]) + '" stroke="' + thkC + '" stroke-width="0.6"/>';
    h += '<line x1="' + f(thkBt[0]) + '" y1="' + f(thkBt[1]) + '" x2="' + f(thkBt[0] + hug) + '" y2="' + f(thkBt[1]) + '" stroke="' + thkC + '" stroke-width="0.6"/>';
    h += '<line x1="' + f(thkPt[0] + hug) + '" y1="' + f(thkPt[1] + 2) + '" x2="' + f(thkBt[0] + hug) + '" y2="' + f(thkBt[1] - 2) + '" stroke="' + thkC + '" stroke-width="1.3" marker-start="url(#bl-arr)" marker-end="url(#bl-arr)"/>';
    h += '<text x="' + f(thkPt[0] + hug + 5) + '" y="' + f((thkPt[1] + thkBt[1]) / 2 + 4) + '" text-anchor="start" font-size="' + ls + '" font-family="Poppins,sans-serif" font-weight="600" fill="' + thkC + '">' + (T ? 'H ' + cmS(T) + ' cm' : 'Höhe') + '</text>';

    /* ── Caption ── */
    var capY = cy + b.my * sc + thickPx + 32;
    var cap = empty ? 'Beispielansicht — gib oben deine Maße ein, die Form passt sich live an'
      : 'Gesamt ' + cmS(g.overallLen) + ' cm lang × ' + cmS(g.overallW) + ' cm breit  ·  ' + (g.shape === 'pill' ? 'gerundete Enden R ' + cmS(g.roundR) + ' cm (auto)' : g.shape === 'dcap' ? 'gerundetes Ende R ' + cmS(g.roundR) + ' cm (auto)' : 'Bogenlänge ' + cmS(g.arcLen) + ' cm (auto)');
    h += '<text x="' + f(cx) + '" y="' + f(capY) + '" text-anchor="middle" font-size="9.5" font-family="Poppins,sans-serif" font-weight="' + (empty ? '500' : '600') + '" fill="' + (empty ? '#999' : lc) + '">' + cap + '</text>';

    svg.innerHTML = h;
  }

  /* ─── Summary ─── */
  function updateSummary() {
    if (!els.summary) return;
    var g = geom(state);
    var parts = [g.shape === 'pill' ? 'Beide Enden rund' : g.shape === 'dcap' ? 'Ein halbrundes Ende' : 'Schräg gerundetes Ende'];
    if (g.valid) parts.push(g.shape === 'single' ? ('B ' + cmS(g.W) + ' cm · Seiten ' + cmS(g.HL) + '/' + cmS(g.HR) + ' cm · H ' + cmS(state.thickness) + ' cm') : ('L ' + cmS(g.length) + ' cm × B ' + cmS(g.W) + ' cm · H ' + cmS(state.thickness) + ' cm'));
    if (state.fabric) parts.push(state.fabric.title);
    if (state.piping && state.piping !== 'none') {
      var pl = LABELS[state.piping];
      if (state.pipingColor && state.pipingColor !== 'match') pl += ' (' + (LABELS[state.pipingColor] || state.pipingColor) + ')';
      parts.push(pl);
    }
    if (state.straps && state.straps !== 'none') parts.push(LABELS[state.straps]);
    if (state.antislip && state.antislip !== 'none') parts.push(LABELS[state.antislip]);
    els.summary.textContent = parts.join(' · ');
  }

  /* ─── Recalc ─── */
  function recalc() {
    var _cf = window.__ucfs_selectedFabric || window._ucfsSelected;
    if (_cf && _cf !== state.fabric) state.fabric = _cf;

    function dv(id) { var e = document.getElementById(id); return e ? parseNum(e.value) / CM_PER_IN : 0; }
    if (state.shape === 'single') { state.w = dv('cc-dim-w'); state.hL = dv('cc-dim-hl'); state.hR = dv('cc-dim-hr'); state.length = 0; }
    else { state.length = dv('cc-dim-len'); state.w = dv('cc-dim-w'); state.hL = 0; state.hR = 0; }
    state.thickness  = dv('cc-dim-t');
    state.extraCovers = els.extraCovers ? els.extraCovers.value : '0';
    state.throwPillow = els.throwPillow ? els.throwPillow.value : 'none';
    state.pillowCovers = els.pillowCovers ? els.pillowCovers.value : 'none';
    state.armrestPillow = els.armrestPillow ? els.armrestPillow.value : 'none';
    state.zipperPlacement = els.zipperPlacement ? els.zipperPlacement.value : '';
    state.pipingColor = els.pipingColor ? els.pipingColor.value : 'match';
    state.notes = els.notes ? els.notes.value : '';

    if (els.pipingColorWrap) {
      var show = (state.piping === 'single' || state.piping === 'double');
      els.pipingColorWrap.style.display = show ? '' : 'none';
      if (!show) { state.pipingColor = 'match'; if (els.pipingColor) els.pipingColor.value = 'match'; }
    }

    var dimsWrap = document.querySelector('.cc-sp-dims:not(.cc-back-dims)');
    if (dimsWrap) { var ins = dimsWrap.querySelectorAll('input'); for (var qi = 0; qi < ins.length; qi++) ins[qi].classList.toggle('ok', parseNum(ins[qi].value) > 0); }

    drawCushion();
    updateSummary();

    var g = geom(state);
    if (els.geomNote) {
      var anyDim = state.w || state.hL || state.hR || state.length;
      if (!g.valid && !anyDim) els.geomNote.textContent = '';
      else if (!g.valid) els.geomNote.textContent = '⚠ ' + (g.errs[0] || 'alle Maße eingeben');
      else if (g.shape === 'pill') els.geomNote.textContent = 'Beide Enden automatisch gerundet — Halbkreise, Radius ' + cmS(g.roundR) + ' cm (= Breite ÷ 2)';
      else if (g.shape === 'dcap') els.geomNote.textContent = 'Ein Ende automatisch gerundet — Halbkreis, Radius ' + cmS(g.roundR) + ' cm (= Breite ÷ 2); anderes Ende gerade';
      else els.geomNote.textContent = 'Gebogenes Ende wird automatisch berechnet — ' + (g.hi === g.lo ? 'gerade (beide Seiten gleich lang)' : 'Viertelbogen, Bogenlänge ' + cmS(g.arcLen) + ' cm, längere Seite ' + (g.tallLeft ? 'oben (links)' : 'unten (rechts)'));
      els.geomNote.style.color = '#8a7a6a';
    }
    if (els.curveHint) {
      els.curveHint.textContent = (state.shape === 'single')
        ? 'ⓘ Das schräge Ende ist ein gleichmäßiger Viertelbogen (Ellipse) von der längeren zur kürzeren Seite — vergleiche ihn mit der Vorschau. Weicht der Bogen deines Polsters deutlich ab, beschreibe ihn unten in der Anmerkung oder schick uns nach der Bestellung ein Foto, dann schneiden wir passend zu.'
        : 'ⓘ Das runde Ende ist ein Halbkreis: Sein Durchmesser entspricht deiner Breite, er ragt also um die halbe Breite über die geraden Seiten hinaus. Vergleiche es mit der Vorschau — ist dein Ende eher oval, spitz oder flach, beschreibe es unten in der Anmerkung oder schick uns ein Foto, dann passen wir es an.';
    }

    var dimsOk = hasDims();
    var ec = parseInt(state.extraCovers) || 0;
    updateExtraCoverLabels();
    var price = dimsOk ? calcPrice() : null;
    var addonTotal = getAddonTotal();

    if (price !== null) {
      els.priceMain.textContent = Math.round(price + addonTotal) + ' €';
      var parts = ['Grundpreis ' + Math.round(price) + ' €'];
      if (ADDON.piping[state.piping])     parts.push('Keder +' + ADDON.piping[state.piping] + ' €');
      if (ADDON.pipingColor[state.pipingColor]) parts.push('Keder-Farbe +' + ADDON.pipingColor[state.pipingColor] + ' €');
      if (ADDON.straps[state.straps])     parts.push('Bänder +' + ADDON.straps[state.straps] + ' €');
      if (ADDON.zipper[state.zipper])     parts.push('Reißverschluss +' + ADDON.zipper[state.zipper] + ' €');
      if (ADDON.antislip[state.antislip]) parts.push('Anti-Rutsch +' + ADDON.antislip[state.antislip] + ' €');
      if (ec > 0)                         parts.push('Zusätzliche Bezüge +' + (extraCoverUnit() * ec) + ' €');
      if (ADDON.throwPillow[state.throwPillow])   parts.push('Dekokissen +' + ADDON.throwPillow[state.throwPillow] + ' €');
      if (ADDON.pillowCovers[state.pillowCovers]) parts.push('Kissenbezüge +' + ADDON.pillowCovers[state.pillowCovers] + ' €');
      if (ADDON.armrestPillow[state.armrestPillow]) parts.push('Armlehnenkissen +' + ADDON.armrestPillow[state.armrestPillow] + ' €');
      els.priceBreak.textContent = parts.join(' · ');
    } else {
      els.priceMain.textContent = '— €';
      els.priceBreak.textContent = 'Alle Maße eingeben, um den Preis zu sehen';
    }

    var complete = isComplete();
    [els.addBtn, els.addBtnSticky].filter(Boolean).forEach(function (btn) {
      btn.disabled = !complete;
      btn.textContent = (complete && price !== null) ? 'Zur Bestellung hinzufügen — ' + Math.round(price + addonTotal) + ' € →' : 'Zur Bestellung hinzufügen →';
    });

    if (els.addNote) {
      if (complete) els.addNote.textContent = 'Kostenloser Versand · Versand in ca. 20 Werktagen';
      else {
        var missing = [];
        if (!dimsOk) missing.push('Maße');
        if (!state.fabric) missing.push('Stoff');
        if (!state.piping) missing.push('Keder');
        if (!state.straps) missing.push('Bänder');
        if (!state.antislip) missing.push('Anti-Rutsch');
        els.addNote.textContent = missing.length ? missing.join(', ') + ' auswählen, um fortzufahren' : '';
      }
    }

    document.dispatchEvent(new CustomEvent('configurator:price-updated', {
      detail: { price: price !== null ? Math.round(price + addonTotal) : null, state: state }
    }));
  }

  function pickCard(el, group, value) {
    el.parentElement.querySelectorAll('.cc-opt-card').forEach(function (c) { c.classList.remove('sel'); });
    el.classList.add('sel');
    state[group] = value;
    if (group === 'shape') buildDims(value);   // swap the dimension inputs for this shape
    recalc();
  }

  /* ─── Line properties for cart ─── */
  function getLineProperties() {
    var ec = parseInt(state.extraCovers) || 0;
    var g = geom(state);
    var price = calcPrice();
    var finalPrice = Math.round(price + getAddonTotal());
    var props = {
      'Form': g.shape === 'pill' ? 'Beide Enden rund' : g.shape === 'dcap' ? 'Ein halbrundes Ende (anderes Ende gerade)' : 'Schräg gerundetes Ende (längere Seite ' + (g.tallLeft ? 'links' : 'rechts') + ')',
      'Gesamtmaß': cmS(g.overallLen) + ' cm lang × ' + cmS(g.overallW) + ' cm breit',
      'Höhe': cmS(state.thickness) + ' cm',
      'Keder': LABELS[state.piping] || state.piping,
      'Keder-Farbe': (state.pipingColor && state.pipingColor !== 'match') ? (LABELS[state.pipingColor] || state.pipingColor) : 'Passend zum Stoff',
      'Bänder': LABELS[state.straps] || state.straps,
      'Reißverschluss': LABELS[state.zipper] || state.zipper,
      'Anti-Rutsch': LABELS[state.antislip] || state.antislip
    };
    if (g.shape === 'single') {
      props['Breite'] = cmS(state.w) + ' cm'; props['Seitenlänge links'] = cmS(state.hL) + ' cm'; props['Seitenlänge rechts'] = cmS(state.hR) + ' cm';
      props['Gebogenes Ende'] = 'Automatischer Viertelbogen · Bogenlänge ' + cmS(g.arcLen) + ' cm';
      props['_Maße (inch)'] = 'W ' + round1(state.w) + ' × HL ' + round1(state.hL) + ' × HR ' + round1(state.hR) + ' × T ' + round1(state.thickness) + '"';
    } else {
      props['Länge'] = cmS(state.length) + ' cm'; props['Breite'] = cmS(state.w) + ' cm';
      props[g.shape === 'pill' ? 'Gerundete Enden' : 'Gerundetes Ende'] = 'Automatisch, Halbkreis' + (g.shape === 'pill' ? 'e' : '') + ' · R ' + cmS(g.roundR) + ' cm';
      props['_Maße (inch)'] = 'L ' + round1(state.length) + ' × W ' + round1(state.w) + ' × T ' + round1(state.thickness) + '"';
    }
    if (state.fabric) {
      props['Stoff'] = state.fabric.title || '';
      props['_Fabric SKU'] = (state.fabric.variants && state.fabric.variants[0] && state.fabric.variants[0].sku) || '';
      props['_Fabric Handle'] = state.fabric.handle || '';
      props['_Fabric Image'] = (state.fabric.images && state.fabric.images[0] && state.fabric.images[0].src) || '';
      var _grade = extractTag(state.fabric.tags || [], 'grade');
      if (_grade) props['Stoffqualität'] = _grade.charAt(0).toUpperCase() + _grade.slice(1);
    }
    if (ec > 0) props['Zusätzliche Bezüge'] = ec + (ec === 1 ? ' Set' : ' Sets');
    if (state.throwPillow !== 'none') props['Dekokissen'] = pillowLabel(state.throwPillow);
    if (state.pillowCovers !== 'none') props['Kissenbezüge'] = pillowLabel(state.pillowCovers);
    if (state.armrestPillow !== 'none') props['Armlehnenkissen'] = ARMREST_LABEL[state.armrestPillow] || state.armrestPillow;
    if (state.zipperPlacement) props['Reißverschluss-Position'] = state.zipperPlacement;
    if (state.notes.trim()) props['Anmerkung'] = state.notes.trim();
    props['_Source Product'] = (document.getElementById('cc-configurator') || { dataset: {} }).dataset.productTitle || document.title.split('–')[0].split('—')[0].trim() || location.pathname;

    var apiPayload = {
      shape: 'bolster', variant: g.shape,
      dims: g.shape === 'single'
        ? { w: state.w, hL: state.hL, hR: state.hR, tallerSide: g.tallLeft ? 'left' : 'right', thickness: state.thickness }
        : { length: state.length, w: state.w, thickness: state.thickness },
      overallLength: +g.overallLen.toFixed(2), overallWidth: +g.overallW.toFixed(2),
      pieces: [{ role: 'bolster', boundingLen: +g.overallLen.toFixed(2), boundingW: +g.overallW.toFixed(2), thickness: state.thickness }],
      piping: state.piping, pipingColor: state.pipingColor, straps: state.straps,
      zipper: state.zipper, antislip: state.antislip,
      extraCovers: ec, throwPillow: state.throwPillow, pillowCovers: state.pillowCovers, armrestPillow: state.armrestPillow
    };
    return { properties: props, price: finalPrice, apiPayload: apiPayload };
  }

  /* ─── Shape-dependent dimension inputs ─── */
  function dimFld(id, label, ph, mn, mx) {
    return '<div class="cc-field">'
      + '<label class="cc-fl" for="cc-dim-' + id + '">' + label + ' <span class="cc-req">*</span></label>'
      + '<input class="cc-fi" id="cc-dim-' + id + '" type="number" min="' + mn + '" max="' + mx + '" placeholder="' + ph + '" inputmode="decimal"></div>';
  }
  function buildDims(shape) {
    var grid = document.querySelector('.cc-sp-dims:not(.cc-back-dims)');
    if (!grid) return;
    grid.style.gridTemplateColumns = (shape === 'single') ? 'repeat(4, 1fr)' : 'repeat(3, 1fr)';
    grid.innerHTML = (shape === 'single')
      ? dimFld('w', 'Breite (cm)', 'z. B. 20', 8, 200) + dimFld('hl', 'Seite links (cm)', 'z. B. 115', 8, 305) + dimFld('hr', 'Seite rechts (cm)', 'z. B. 95', 8, 305) + dimFld('t', 'Höhe (cm)', 'z. B. 7,5', 3, 25)
      : dimFld('len', 'Länge (cm)', 'z. B. 75', 10, 405) + dimFld('w', 'Breite (cm)', 'z. B. 25', 8, 200) + dimFld('t', 'Höhe (cm)', 'z. B. 7,5', 3, 25);
    var note = document.createElement('div');
    note.id = 'cc-bl-geomnote';
    note.style.cssText = 'grid-column:1/-1;font-size:11px;font-family:Poppins,sans-serif;color:#8a7a6a;padding:2px 2px 0;line-height:1.4';
    grid.appendChild(note);
    els.geomNote = note;
    var hint = document.createElement('div');
    hint.id = 'cc-bl-curvehint';
    hint.style.cssText = 'grid-column:1/-1;font-size:10.5px;font-family:Poppins,sans-serif;color:#8a8a85;padding:3px 2px 0;line-height:1.5';
    grid.appendChild(hint);
    els.curveHint = hint;
  }
  /* Shape-card product renders (Shopify Files) */
  var SHAPE_IMG = {
    single: 'https://cdn.shopify.com/s/files/1/0676/5687/6232/files/bolster-shape-angled-end_png.png?v=1791963921',
    dcap:   'https://cdn.shopify.com/s/files/1/0676/5687/6232/files/bolster-shape-one-round-end.png?v=1791963921',
    pill:   'https://cdn.shopify.com/s/files/1/0676/5687/6232/files/bolster-shape-both-round-ends.png?v=1791963920'
  };
  function shapeImg(val, alt) {
    return '<img src="' + SHAPE_IMG[val] + '" alt="' + alt + '" loading="lazy" style="width:100%;height:100%;object-fit:cover;object-position:center;display:block">';
  }
  function shapeCard(val, name, sel) {
    return '<div class="cc-opt-card' + (sel ? ' sel' : '') + '" data-group="shape" data-value="' + val + '">'
      + '<div class="cc-opt-img" style="background:#F5F1E8">' + shapeImg(val, name) + '</div>'
      + '<div class="cc-opt-name">' + name + '</div><div class="cc-opt-price">inklusive</div></div>';
  }

  /* ─── DOM transformation (adapt shared Liquid section) ─── */
  function transformDOM() {
    var backDims = document.querySelectorAll('.cc-back-dims');
    for (var i = 0; i < backDims.length; i++) backDims[i].style.display = 'none';
    var seatLabel = document.querySelector('.cc-seat-label');
    if (seatLabel) seatLabel.style.display = 'none';

    /* Shape step: two cards — one rounded end / both ends rounded */
    var shapeSec = document.getElementById('cc-shape-section');
    if (shapeSec) shapeSec.style.display = '';
    var shapeGrid = document.getElementById('cc-shape-grid');
    if (shapeGrid) {
      shapeGrid.className = 'cc-opt-grid';
      shapeGrid.innerHTML = shapeCard('single', 'Schräg gerundetes Ende', state.shape === 'single')
        + shapeCard('dcap', 'Ein halbrundes Ende', state.shape === 'dcap')
        + shapeCard('pill', 'Beide Enden rund', state.shape === 'pill');
    }

    buildDims(state.shape);

    /* Guidance line above the Notes field for non-standard curves */
    var notesEl = document.getElementById('cc-notes');
    if (notesEl && !document.getElementById('cc-bl-noteshint')) {
      var nh = document.createElement('div');
      nh.id = 'cc-bl-noteshint';
      nh.style.cssText = 'font-size:11px;font-family:Poppins,sans-serif;color:#8a7a6a;margin-bottom:6px;line-height:1.5';
      nh.textContent = 'Ungewöhnlicher Bogen oder Sonderform? Beschreib sie hier — oder bestelle und schick uns ein Foto bzw. eine Papierschablone, dann schneiden wir passend zu deinem Möbel zu.';
      notesEl.parentNode.insertBefore(nh, notesEl);
    }

    var svg = document.getElementById('cc-dim-svg');
    if (svg) { svg.setAttribute('viewBox', '0 0 440 290'); svg.style.maxHeight = '280px'; svg.style.width = '100%'; }
  }

  /* ─── Init ─── */
  function init() {
    var wrap = document.getElementById('cc-configurator');
    if (!wrap) return;
    var ft = (wrap.getAttribute('data-formula-type') || '').replace('_cover', '');
    if (ft && ft !== 'bolster') return;

    /* Per-product default shape (block.liquid passes the cushion.bolster_shape metafield) */
    var ds = (wrap.getAttribute('data-default-shape') || '').toLowerCase();
    if (ds === 'single' || ds === 'pill' || ds === 'dcap') state.shape = ds;

    transformDOM();

    els.priceMain    = document.getElementById('cc-price-main');
    els.priceBreak   = document.getElementById('cc-price-break');
    els.summary      = document.getElementById('cc-sp-summary');
    els.geomNote     = document.getElementById('cc-bl-geomnote');
    els.extraCovers  = document.getElementById('cc-extra-covers');
    els.throwPillow  = document.getElementById('cc-throw-pillow');
    els.pillowCovers = document.getElementById('cc-pillow-covers');
    els.armrestPillow = document.getElementById('cc-armrest-pillow');
    els.zipperPlacement = document.getElementById('cc-zipper-placement');
    els.pipingColor    = document.getElementById('cc-piping-color');
    els.pipingColorWrap = document.getElementById('cc-piping-color-wrap');
    els.notes        = document.getElementById('cc-notes');
    els.addBtn       = document.getElementById('cc-add-btn');
    els.addBtnSticky = document.getElementById('cc-add-btn-sticky');
    els.addNote      = document.getElementById('cc-add-note');
    els.dimSvg       = document.getElementById('cc-dim-svg');

    wrap.addEventListener('click', function (e) {
      var card = e.target.closest('.cc-opt-card');
      if (!card || !card.dataset.group) return;
      pickCard(card, card.dataset.group, card.dataset.value);
    });

    // Delegated input listener on the dims grid — survives rebuilds when the shape changes
    var dimsGridEl = document.querySelector('.cc-sp-dims:not(.cc-back-dims)');
    if (dimsGridEl) dimsGridEl.addEventListener('input', recalc);
    if (els.extraCovers) els.extraCovers.addEventListener('change', recalc);
    if (els.throwPillow) els.throwPillow.addEventListener('change', recalc);
    if (els.pillowCovers) els.pillowCovers.addEventListener('change', recalc);
    if (els.armrestPillow) els.armrestPillow.addEventListener('change', recalc);
    if (els.zipperPlacement) els.zipperPlacement.addEventListener('change', recalc);
    if (els.pipingColor) els.pipingColor.addEventListener('change', recalc);
    if (els.notes) els.notes.addEventListener('input', recalc);

    document.addEventListener('fabric:selected', function (e) { state.fabric = e.detail; recalc(); });
    var existingFabric = window.__ucfs_selectedFabric || window._ucfsSelected;
    if (!state.fabric && existingFabric) { state.fabric = existingFabric; recalc(); }
    setInterval(function () {
      var fb = window.__ucfs_selectedFabric || window._ucfsSelected;
      if (fb && fb !== state.fabric) { state.fabric = fb; recalc(); }
    }, 800);

    function handleAdd() {
      if (!isComplete()) return;
      document.dispatchEvent(new CustomEvent('configurator:add-to-cart', { detail: getLineProperties() }));
    }
    if (els.addBtn) els.addBtn.addEventListener('click', handleAdd);
    if (els.addBtnSticky) els.addBtnSticky.addEventListener('click', handleAdd);

    var defaultZipper = wrap.querySelector('.cc-opt-card[data-group="zipper"][data-value="regular"]');
    if (defaultZipper) defaultZipper.classList.add('sel');

    drawCushion();
    recalc();
  }

  window.BolsterCushionConfigurator = {
    getState: function () { return state; },
    getLineProperties: getLineProperties,
    recalc: recalc,
    geom: function () { return geom(state); }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
